import React from 'react';
import { FormControl, InputAdornment, MenuItem, Select, TextField, Box } from '@mui/material';
import { Icon } from '@iconify/react';
import { filterControlSx, filterMenuProps, filterSelectMinWidth } from '../styles/filterStyles';
import { primaryIconColor } from '../styles/iconStyles';

// The two controls every list screen's filter bar is built from. They existed as near-identical
// private copies in user.jsx and financialSupport.jsx, which is how the two bars drifted apart;
// keeping one copy is what stops them drifting again.

// Options may be plain strings or { value, label, count } objects: some screens filter on a raw
// value that is already presentable (a region name), others on a code that needs humanizing (a
// persona). `count` is optional: when given, the menu shows how many rows each choice leaves.
const normalizeOption = (option) =>
  option !== null && typeof option === 'object' ? option : { value: option, label: option };

const countBadgeSx = {
  ml: 'auto',
  pl: 2,
  fontSize: 13,
  lineHeight: '20px',
  fontWeight: 500,
  color: '#6C6A86',
  fontVariantNumeric: 'tabular-nums',
};

// `testId` lands on the element that carries the behaviour — the combobox you
// click, the input you type into — never on a wrapper around it. A test id on a
// wrapper reads as if it identifies the control, and then `input[data-testid=x]`
// silently matches nothing; that cost a full day on the eK Market suite.
export function FilterSelect({
  value,
  onChange,
  placeholder,
  options = [],
  minWidth = filterSelectMinWidth,
  testId,
  disabled = false,
}) {
  const normalized = options.map(normalizeOption);

  return (
    <FormControl sx={{ ...filterControlSx, minWidth, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
      <Select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        displayEmpty
        disabled={disabled}
        MenuProps={filterMenuProps}
        // SelectDisplayProps, not inputProps: this puts the id on the div that
        // renders as role="combobox" and receives the click, rather than on
        // MUI's hidden native input.
        SelectDisplayProps={testId ? { 'data-testid': testId } : undefined}
        IconComponent={(iconProps) => (
          <Icon icon="mdi:chevron-down" width="20" height="20" color={primaryIconColor} {...iconProps} />
        )}
        // Falls back to the raw value so a filter held from a previous load still reads as itself
        // rather than blank if its option is no longer in the list.
        renderValue={(selected) =>
          selected ? normalized.find((option) => option.value === selected)?.label || selected : placeholder
        }
      >
        <MenuItem value="">{placeholder}</MenuItem>
        {normalized.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
            {typeof option.count === 'number' && (
              <Box component="span" sx={countBadgeSx}>{option.count}</Box>
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// A row of pill toggles for the one dimension a reviewer switches most (a status, a type), each
// with its row count, so the split of the list is visible before anything is clicked. Exactly one
// is selected; "All" is the option whose value is ''.
export function FilterChips({ options = [], value, onChange, ariaLabel, testId }) {
  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value || 'all'}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={testId ? `${testId}-${option.value || 'all'}` : undefined}
            onClick={() => onChange(option.value)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              height: 36,
              px: 1.75,
              borderRadius: '999px',
              border: '1px solid',
              borderColor: selected ? '#4F8506' : '#E4E6F1',
              backgroundColor: selected ? '#EEF7E6' : '#FFFFFF',
              color: selected ? '#3B6404' : '#33343D',
              fontFamily: 'inherit',
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              fontWeight: selected ? 600 : 500,
              cursor: 'pointer',
              transition: 'background-color 120ms, border-color 120ms',
              '&:hover': { borderColor: '#4F8506' },
              '&:focus-visible': { outline: '2px solid #4F8506', outlineOffset: 2 },
            }}
          >
            <span>{option.label}</span>
            {typeof option.count === 'number' && (
              <Box
                component="span"
                sx={{
                  minWidth: 22,
                  px: 0.75,
                  borderRadius: '999px',
                  backgroundColor: selected ? '#FFFFFF' : '#F1F1F1',
                  fontSize: 12,
                  lineHeight: '20px',
                  fontWeight: 600,
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {option.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// `sx` is for layout only — how much room the field takes on its particular bar. The pill itself
// comes from filterControlSx and is applied first so it cannot be styled away by a caller.
export function FilterSearchField({
  value, onChange, placeholder = 'Search', minWidth = 280, flexBasis = 320, sx, testId,
}) {
  return (
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      // inputProps (lower-case i) reaches the <input> itself. InputProps would
      // put it on the wrapper — see the note on FilterSelect above.
      inputProps={testId ? { 'data-testid': testId } : undefined}
      sx={{
        ...filterControlSx,
        // Takes the slack on the bar so the dropdowns keep their natural width, and drops to its
        // own full-width row on a phone rather than squeezing to a few characters.
        flex: { xs: '1 1 100%', md: `1 1 ${flexBasis}px` },
        minWidth: { xs: '100%', md: minWidth },
        ...sx,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Icon icon="material-symbols:search-rounded" width="20" height="20" color={primaryIconColor} />
          </InputAdornment>
        ),
        // Clearing a search by selecting the text and deleting it is fiddly on touch, and a long
        // query is exactly when a reviewer wants out of it fastest.
        endAdornment: value ? (
          <InputAdornment position="end">
            <Box
              component="button"
              type="button"
              aria-label="Clear search"
              onClick={() => onChange('')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: 0,
                p: 0,
                background: 'none',
                cursor: 'pointer',
                color: '#6C6A86',
              }}
            >
              <Icon icon="material-symbols:close-rounded" width="18" height="18" />
            </Box>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}
